import React from "react";
import { motion } from "framer-motion";
import { Shield, Cloud, Cpu, Lock, Wifi, Database, KeyRound } from "lucide-react";

// 6 domain nodes around the shield core
const NODES = [
  { icon: <Cloud className="h-5 w-5" />, label: "Cloud", angle: -90, color: "#38BDF8", target: "cloud" },
  { icon: <Cpu className="h-5 w-5" />, label: "AI Security", angle: -30, color: "#0F4C81", target: "cybersecurity" },
  { icon: <Lock className="h-5 w-5" />, label: "Data", angle: 30, color: "#F97316", target: "cybersecurity" },
  { icon: <Wifi className="h-5 w-5" />, label: "Network", angle: 90, color: "#38BDF8", target: "managed-security" },
  { icon: <Database className="h-5 w-5" />, label: "Backup", angle: 150, color: "#0F4C81", target: "backup" },
  { icon: <KeyRound className="h-5 w-5" />, label: "Identity", angle: 210, color: "#F97316", target: "managed-security" },
];

export default function HeroGraphic() {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(`service-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="relative w-full aspect-square max-w-[600px] mx-auto" data-testid="hero-graphic">
      {/* Ambient glow behind the ring */}
      <div className="absolute inset-6 rounded-full bg-gradient-to-br from-primary/25 via-accent/15 to-secondary/20 blur-3xl" />

      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="core2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.45" />
            <stop offset="70%" stopColor="#0F4C81" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#0F4C81" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38BDF8" />
            <stop offset="60%" stopColor="#0F4C81" />
            <stop offset="100%" stopColor="#F97316" />
          </linearGradient>
          <linearGradient id="ringGrad2" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#38BDF8" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background halo */}
        <circle cx="200" cy="200" r="180" fill="url(#core2)" />

        {/* Rotating dashed rings */}
        <g className="rotate-slow" style={{ transformOrigin: "200px 200px" }}>
          <circle cx="200" cy="200" r="160" fill="none" stroke="url(#ringGrad)" strokeWidth="1.2" strokeDasharray="4 8" opacity="0.6" />
        </g>
        <g style={{ transformOrigin: "200px 200px", animation: "rotate-slow 55s linear infinite reverse" }}>
          <circle cx="200" cy="200" r="130" fill="none" stroke="url(#ringGrad2)" strokeWidth="1" strokeDasharray="2 12" opacity="0.5" />
          <circle cx="200" cy="200" r="105" fill="none" stroke="#F97316" strokeWidth="1" strokeDasharray="2 6" opacity="0.35" />
        </g>

        {/* Core hex outline */}
        <polygon
          points="200,120 275,160 275,240 200,280 125,240 125,160"
          fill="none"
          stroke="url(#ringGrad)"
          strokeWidth="1.5"
          opacity="0.4"
        />

        {/* Connector data-flow lines to each node */}
        {NODES.map((n, i) => {
          const rad = (n.angle * Math.PI) / 180;
          const x2 = 200 + Math.cos(rad) * 160;
          const y2 = 200 + Math.sin(rad) * 160;
          return (
            <g key={n.label}>
              <line
                x1="200"
                y1="200"
                x2={x2}
                y2={y2}
                stroke={n.color}
                strokeOpacity="0.28"
                strokeWidth="1"
                strokeDasharray="3 4"
              />
              {/* Animated data packet along the line */}
              <motion.circle
                r="3"
                fill={n.color}
                filter="url(#glow)"
                initial={{ cx: 200, cy: 200 }}
                animate={{ cx: [200, x2, 200], cy: [200, y2, 200] }}
                transition={{ duration: 3.5, delay: i * 0.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </g>
          );
        })}

        {/* Floating particles */}
        {[...Array(12)].map((_, i) => {
          const ang = (i * 30 * Math.PI) / 180;
          const r = 180 + (i % 3) * 8;
          return (
            <motion.circle
              key={i}
              cx={200 + Math.cos(ang) * r}
              cy={200 + Math.sin(ang) * r}
              r="1.2"
              fill={i % 2 ? "#F97316" : "#38BDF8"}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.7, 0] }}
              transition={{ duration: 3, delay: i * 0.25, repeat: Infinity }}
            />
          );
        })}
      </svg>

      {/* Central shield with double-halo */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <div className="absolute -inset-6 rounded-full bg-primary/25 blur-2xl" />
          <div className="absolute -inset-3 rounded-full border border-white/25 dark:border-white/15" />
          <div className="relative h-36 w-36 sm:h-40 sm:w-40 rounded-[2rem] bg-gradient-to-br from-primary via-primary/95 to-accent shadow-2xl shadow-primary/40 grid place-items-center border border-white/15">
            <Shield className="h-16 w-16 sm:h-20 sm:w-20 text-white" strokeWidth={1.3} />
          </div>
          <div className="absolute inset-x-0 -bottom-9 grid place-items-center pointer-events-none">
            <div className="rounded-full bg-background/90 backdrop-blur border border-primary/25 shadow-lg px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
              Zero-Trust Core
            </div>
          </div>
        </motion.div>
      </div>

      {/* Orbiting clickable nodes */}
      {NODES.map((n, i) => {
        const rad = (n.angle * Math.PI) / 180;
        const x = 50 + Math.cos(rad) * 40;
        const y = 50 + Math.sin(rad) * 40;
        return (
          <motion.button
            key={n.label}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35 + i * 0.09, duration: 0.55 }}
            style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
            onClick={scrollTo(n.target)}
            data-testid={`hero-orbit-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-btn`}
            aria-label={`Explore ${n.label}`}
            className="absolute group focus:outline-none"
          >
            <div className="relative">
              <div
                className="absolute inset-0 rounded-2xl ping-ring border-2"
                style={{ borderColor: n.color, animationDelay: `${i * 0.35}s` }}
              />
              <div
                className="relative h-16 w-16 rounded-2xl glass grid place-items-center border shadow-xl float-y transition-transform group-hover:scale-110 group-hover:shadow-2xl"
                style={{ animationDelay: `${i * 0.4}s`, borderColor: `${n.color}70`, color: n.color }}
              >
                {n.icon}
              </div>
              <div className="mt-1.5 text-center text-[11px] font-bold uppercase tracking-wide text-foreground/85 group-hover:text-foreground transition-colors">
                {n.label}
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}
